import type { ReactNode } from "react"
import { useMemo, useState } from "react"
import { createPortal } from "react-dom"

import { useCabinetAccess, useHasPermission } from "../../access/use-access"
import { fetchJournal } from "../../api/journal.api"
import { cx } from "../../lib/cx"
import { ProblemScreen } from "../../shell/states"
import { resolveBranch } from "../../shell/use-branch"
import { Button, Chip, Chips, Skeleton, Switch } from "../../ui"
import { useResource } from "../directories/use-resource"
import {
  PRINT_MARKS,
  type PrintGrid,
  type PrintList,
  type PrintMode,
  buildPrintGrid,
  buildPrintList,
  printDateWords,
  printedAtWords,
  sheetsWords,
} from "./print-model"
import styles from "./print-screen.module.css"

/**
 * 2-2.12 «Печать расписания» (макет П2, ТЗ 02 §2.12).
 *
 * Слева — что печатаем: день по мастерам (сетка, A4 альбомная) или день одного мастера
 * (список, A4 книжная), телефоны клиентов — переключателем, по умолчанию выключенным.
 * Справа — листы в том виде, в каком выйдут из принтера: ч/б, со знаками статусов.
 *
 * ПЕЧАТАЕТСЯ НЕ ЭКРАН. Листы рисуются второй раз — порталом прямо в body, и при печати
 * скрывается всё остальное (print-screen.module.css): оболочка кабинета на бумаге дала бы
 * меню, шапку и пустые страницы. Каждый лист — отдельная страница: разрыв страницы
 * не режет строку записи пополам.
 */
export function JournalPrintScreen(props: {
  readonly date: string
  readonly branchId: string | null
  readonly onClose: () => void
}): ReactNode {
  const { context } = useCabinetAccess()
  const canSeePhones = useHasPermission("clients.phone.view")
  const branch = resolveBranch(context, props.branchId)
  const [mode, setMode] = useState<PrintMode>("grid")
  const [employeeId, setEmployeeId] = useState<string | null>(null)
  const [phones, setPhones] = useState(false)
  const [printedAt, setPrintedAt] = useState(() => new Date())

  const schedule = useResource(
    branch === null
      ? null
      : (signal) =>
          fetchJournal({ branchId: branch.id, dateFrom: props.date, dateTo: props.date, includeCancelled: false }, signal),
    branch === null ? null : `print-${branch.id}-${props.date}`,
  )

  const result = schedule.data
  const grid: PrintGrid | null = useMemo(
    () => (result === null ? null : buildPrintGrid(result, props.date)),
    [result, props.date],
  )
  const employeeIds = useMemo(
    () =>
      result === null
        ? []
        : result.employees.toSorted((a, b) => a.displayName.localeCompare(b.displayName, "ru")).map((item) => item.id),
    [result],
  )
  const selected = employeeId ?? employeeIds[0] ?? null
  const list: PrintList | null = useMemo(
    () => (result === null || selected === null ? null : buildPrintList(result, props.date, selected, phones && canSeePhones)),
    [result, props.date, selected, phones, canSeePhones],
  )

  if (branch === null) {
    return (
      <div className={styles.screen}>
        <p className={styles.hint}>Вам не открыт ни один филиал — печатать нечего.</p>
      </div>
    )
  }
  if (schedule.problem !== null && result === null) {
    return <ProblemScreen problem={schedule.problem} onRetry={schedule.reload} />
  }

  const timeZone = branch.timezone
  const sheetsCount = mode === "grid" ? (grid?.sheets.length ?? 1) : (list?.sheets.length ?? 1)
  const phonesShown = mode === "list" && phones && canSeePhones

  const header = (
    <div className={styles.sheetHeader}>
      <div>
        <b className={styles.salon}>{context.tenant.name}</b> · {branch.name}
      </div>
      <div className={styles.printed}>
        {printDateWords(props.date)} · {printedAtWords(printedAt, timeZone)}
      </div>
    </div>
  )

  const sheets =
    result === null ? null : mode === "grid" && grid !== null ? (
      grid.sheets.map((columns, sheetIndex) => (
        <section key={sheetIndex} className={cx(styles.sheet, styles.landscape)}>
          {header}
          <div
            className={styles.grid}
            style={{ gridTemplateColumns: `44px repeat(${Math.max(1, columns.length)}, minmax(0, 1fr))` }}
          >
            <div className={styles.corner} />
            {columns.map((column) => (
              <div key={column.name} className={styles.colHead}>
                {column.name}
                <div className={styles.colWork}>{column.work}</div>
              </div>
            ))}
            {grid.hours.map((hour, hourIndex) => (
              <GridRow key={hour} hour={hour} cells={columns.map((column) => column.cells[hourIndex])} />
            ))}
          </div>
          <div className={styles.legend}>
            <span><b>✓</b> завершён</span>
            <span><b>▸</b> в работе</span>
            <span><b>?</b> ждёт подтверждения</span>
            <span><b>—</b> не пришёл (штриховка)</span>
            <span>■ перерыв, блокировка (серый)</span>
            <span>без знака — подтверждена</span>
          </div>
        </section>
      ))
    ) : list !== null ? (
      list.sheets.map((rows, sheetIndex) => (
        <section key={sheetIndex} className={cx(styles.sheet, styles.portrait)}>
          {header}
          <div className={styles.listTitle}>{list.title}</div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.colTime}>Время</th>
                <th>Клиент</th>
                {phonesShown ? <th>Телефон</th> : null}
                <th>Услуга</th>
                <th className={styles.colMin}>Мин</th>
                <th>Комментарий</th>
                <th className={styles.colStatus}>Статус</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={phonesShown ? 7 : 6} className={styles.emptyRow}>
                    Записей на этот день нет
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={`${row.time}-${index}`} className={styles[`row-${row.kind}`]}>
                    <td className={styles.strong}>{row.time}</td>
                    <td className={styles.strong}>{row.client}</td>
                    {phonesShown ? <td className={styles.num}>{row.phone}</td> : null}
                    <td>{row.service}</td>
                    <td>{row.minutes}</td>
                    <td>{row.comment}</td>
                    <td>{row.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      ))
    ) : null

  return (
    <div className={styles.screen}>
      <style>{`@page { size: A4 ${mode === "grid" ? "landscape" : "portrait"}; margin: 8mm; }`}</style>
      <aside className={styles.panel}>
        <div className={styles.panelTitle}>Что печатаем</div>
        <Chips label="Что печатаем" className={styles.modes}>
          <Chip active={mode === "grid"} onClick={() => setMode("grid")}>
            День по мастерам · сетка · альбомная
          </Chip>
          <Chip active={mode === "list"} onClick={() => setMode("list")}>
            День одного мастера · список · книжная
          </Chip>
        </Chips>

        {mode === "list" && employeeIds.length > 0 ? (
          <label className={styles.master}>
            <span>Мастер</span>
            <select value={selected ?? ""} onChange={(event) => setEmployeeId(event.target.value)}>
              {employeeIds.map((id) => (
                <option key={id} value={id}>
                  {result?.employees.find((item) => item.id === id)?.displayName ?? ""}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <div className={styles.divider}>
          <Switch
            className={styles.switchRow}
            checked={phonesShown}
            disabled={!canSeePhones || mode === "grid"}
            onChange={setPhones}
          >
            Телефоны клиентов
          </Switch>
          <small className={phonesShown ? styles.hintWarn : styles.hint}>
            {!canSeePhones
              ? "Телефоны клиентов вам открываются только по одному, с записью в журнал, — на печать они не выводятся."
              : phonesShown
                ? "Распечатка с телефонами — вынос персональных данных за пределы системы. Отвечает салон как оператор."
                : "По умолчанию выключено. В сетке телефоны не печатаются никогда — только в списке мастера."}
          </small>
        </div>

        <div className={cx(styles.divider, styles.hint)}>
          Отменённые не печатаются; неявки — с отметкой; перерывы и блокировки — наравне с записями, иначе распечатка
          врёт. Разрывы страниц не режут карточки.
        </div>

        <Button
          variant="primary"
          disabled={result === null}
          onClick={() => {
            setPrintedAt(new Date())
            // Время печати — в шапке листа; даём React дорисовать его до вызова печати.
            window.setTimeout(() => window.print(), 0)
          }}
        >
          {sheetsWords(sheetsCount)}
        </Button>
        <Button onClick={props.onClose}>К журналу</Button>
      </aside>

      <div className={styles.preview}>
        {schedule.loading && result === null ? <Skeleton width={1000} height={707} radius={4} /> : sheets}
      </div>

      {sheets === null ? null : createPortal(<div className={cx("plnr-print-host", styles.printOnly)}>{sheets}</div>, document.body)}
    </div>
  )
}

function GridRow(props: {
  readonly hour: number
  readonly cells: readonly ({ readonly items: readonly { readonly kind: string; readonly title: string; readonly sub: string }[]; readonly off: boolean } | undefined)[]
}): ReactNode {
  return (
    <>
      <div className={styles.hour}>{`${String(props.hour).padStart(2, "0")}:00`}</div>
      {props.cells.map((cell, index) => {
        const lead = cell?.items[0]
        return (
          <div
            key={index}
            className={cx(styles.cell, cell?.off === true && styles.off, lead !== undefined && styles[`cell-${lead.kind}`])}
          >
            {cell?.items.map((item, itemIndex) => (
              <div key={itemIndex} className={item.kind === "cont" ? styles.cont : undefined}>
                <div className={cx(styles.cellTitle, item.kind === "work" && styles.bold)}>
                  {PRINT_MARKS[item.kind as keyof typeof PRINT_MARKS] ?? ""}
                  {item.title}
                </div>
                {item.sub === "" ? null : <div className={styles.cellSub}>{item.sub}</div>}
              </div>
            ))}
          </div>
        )
      })}
    </>
  )
}
